import { graphqlRequest } from "@/graphql/client";
import { invokeAgent as invokeAgentQuery } from "@/graphql/queries";
import { invokeExitStrategy } from "@/graphql/invokeAgent";
import { onExitStrategyResponse } from "@/graphql/subscriptions";
import { MODEL_KEYS, resolveModelKey } from "@/constants/modelRegistry";
import { PROCESS_STATUS } from "@/store/subscriptionCore/processConstants";
import { createAppSyncSubscription, isSuccessStatusCode, parseGraphqlBody } from "@/store/subscriptionCore/subscriptionApi";
import { propertyIdToDetailsPath } from "@/utils/propertyUrlUtils";

/**
 * @param {Object} params
 * @param {string} params.propertyId
 * @param {string} params.userId
 * @param {string} params.customerId
 * @param {Array<string>} [params.strategies]
 * @param {string} [params.modelName] - Superadmin-selected override. Blank / unknown keys fall back to gpt-5.4.
 * @param {Object} context
 * @returns {Promise<Object>}
 */
export async function runExitStrategyProcess(params, context) {
  const { propertyId, userId, customerId, strategies, modelName } = params;
  context.setProgress({ status: PROCESS_STATUS.INVOKING, message: "Starting exit strategy analysis..." });

  const resolvedModelName = resolveModelKey(modelName, MODEL_KEYS.GPT_5_4);
  const invokeVariables = invokeExitStrategy({
    propertyId,
    userId,
    customerId,
    strategies,
    modelName: resolvedModelName,
  });

  const response = await graphqlRequest({
    query: invokeAgentQuery,
    variables: invokeVariables,
    authMode: "AMAZON_COGNITO_USER_POOLS",
  });

  const invokeResult = response?.data?.invokeAgent;
  if (!isSuccessStatusCode(invokeResult?.statusCode)) {
    throw new Error(`Exit strategy invocation failed: ${invokeResult?.statusCode ?? "unknown"}`);
  }
  const correlationId = invokeResult?.id;
  if (!correlationId) {
    throw new Error("No response ID returned from exit strategy invocation");
  }
  context.setCorrelationId(correlationId);

  const streamedStrategies = [];

  return new Promise((resolve, reject) => {
    const { teardown } = createAppSyncSubscription({
      subscriptionQuery: onExitStrategyResponse,
      correlationId,
      processTag: "ExitStrategy",
      onEvent: (event) => {
        const body = parseGraphqlBody(event?.body);
        if (event?.status === "processing") {
          if (body?.strategy) {
            streamedStrategies.push(body.strategy);
          }
          context.setProgress({
            status: PROCESS_STATUS.PROCESSING,
            message: body?.message || "Processing exit strategy analysis...",
          });
          return;
        }

        if (event?.status === "error") {
          teardown();
          reject(new Error(body?.error || "Exit strategy analysis failed"));
          return;
        }

        if (event?.status === "done" && isSuccessStatusCode(event?.statusCode)) {
          teardown();
          resolve({
            correlationId,
            payload: {
              strategies: Array.isArray(body?.strategies) ? body.strategies : streamedStrategies,
              body,
            },
            notification: {
              outcome: "success",
              message: "Exit strategy analysis completed.",
              actionRoute: propertyIdToDetailsPath(propertyId),
              actionLabel: "View Property",
            },
          });
          return;
        }

        teardown();
        reject(new Error(`Unexpected terminal event: ${event?.statusCode ?? "unknown"} / ${event?.status ?? "unknown"}`));
      },
      onError: reject,
    });
    context.registerTeardown(teardown);
  });
}
